import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, KanbanSquareIcon, ListChecksIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { updateLeadStage } from '../../services/leadService';
import { usePreferences } from '../../hooks/usePreferences';
import { formatDate, fullName } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageManager } from '../../components/crm/StageManager';
import { noteColor } from '../../components/crm/noteColors';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { FloatingPanel } from '../../components/shared/FloatingPanel';
import { Switch } from '../../components/ui/Switch';
import { Checkbox } from '../../components/ui/Checkbox';

/** Campos que se pueden mostrar en cada tarjeta del embudo. */
const CARD_FIELDS: {
  key: string;
  label: string;
}[] = [{
  key: 'company',
  label: 'Empresa'
}, {
  key: 'phone',
  label: 'Teléfono'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'budget',
  label: 'Presupuesto'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'note',
  label: 'Última nota'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'date',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}];
export function Pipeline() {
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    notes,
    isDemo
  } = useWorkspace();
  const [preferences, updatePreferences] = usePreferences();
  const cards = preferences.pipelineCards;
  const [dragging, setDragging] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [hidden, setHidden] = useState<string[]>([]);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const canEdit = hasPermission('leads.edit');
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const compact = cards.mode === 'compacto';
  function setCards(patch: Partial<typeof cards>) {
    updatePreferences({
      pipelineCards: {
        ...cards,
        ...patch
      }
    });
  }
  function shows(field: string) {
    if (compact) return field === 'company' || field === 'phone';
    return cards.fields.includes(field);
  }
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  async function drop(stageId: string) {
    if (dragging) await updateLeadStage(dragging, stageId, authorName);
    setDragging(null);
    setOverStage(null);
  }
  const columns = stages.filter(s => s.active && !hidden.includes(s.id));
  if (leads.length === 0) {
    return <>
        <PageHeader title="Embudo de ventas" description="Tus oportunidades por estado." data-id="element-1322" data-mp="3quvz34ci3vl4" />
        <EmptyState icon={<KanbanSquareIcon className="h-5 w-5" data-id="element-1324" data-mp="3vtpe517frune" />} title="Tu embudo está vacío" description="Cuando recibas leads desde tus landings aparecerán aquí para que puedas moverlos entre estados." action={<Link to="/app/landings" data-id="element-1325" data-mp="9obw1jv9frym">
              <Button data-id="element-1326" data-mp="9naaumkj5p36">Crear una landing</Button>
            </Link>} data-id="element-1323" data-mp="3vuh7s6hh3ib1" />
      </>;
  }
  return <>
      <PageHeader title="Embudo de ventas" description="Arrastra cada lead a su nuevo estado." actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1328" data-mp="ii6rg06ky412" />}
            <Button variant="secondary" leftIcon={<SlidersHorizontalIcon className="h-4 w-4" data-id="element-1330" data-mp="nh1j7rmiz2xb" />} onClick={() => setCardsOpen(true)} data-id="element-1329" data-mp="ndvvogtjo93p">
              Personalizar tarjetas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1332" data-mp="xdczesp8kcx1" />} onClick={() => setStagesOpen(true)} data-id="element-1331" data-mp="xcboco87zqfb">
              Gestionar estados
            </Button>
          </>} data-id="element-1327" data-mp="dnkmofv079py" />

      {/* Columnas ocultas */}
      {stages.filter(s => s.active).length > columns.length && <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-4 py-3" data-id="element-1333" data-mp="23u1nm9fcnyfl">
          <span className="text-xs font-medium text-muted-foreground" data-id="element-1334" data-mp="1yuyhsmrjpo6k">
            Columnas ocultas:
          </span>
          {stages.filter(s => s.active && hidden.includes(s.id)).map(stage => <button key={stage.id} type="button" onClick={() => setHidden(prev => prev.filter(id => id !== stage.id))} className="inline-flex items-center gap-1.5 rounded-full bg-background px-2.5 py-1 text-xs font-medium text-foreground" data-id="element-1335" data-mp="1672lvk9sw0au">
                <EyeIcon className="h-3 w-3" aria-hidden="true" data-id="element-1336" data-mp="1g35r4yzy8f20" />
                {stage.name}
              </button>)}
        </div>}

      <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0" data-id="element-1337" data-mp="2vnbvjiw3bfaf">
        <div className="flex min-w-max gap-4" data-id="element-1338" data-mp="2rjm7qvn8p1qg">
          {columns.map(stage => {
          const stageLeads = leads.filter(l => l.stageId === stage.id);
          return <section key={stage.id} onDragOver={e => {
            if (!canEdit) return;
            e.preventDefault();
            setOverStage(stage.id);
          }} onDragLeave={() => setOverStage(null)} onDrop={() => drop(stage.id)} className={`w-[280px] flex-none rounded-2xl border bg-surface p-3 transition-colors ${overStage === stage.id ? 'border-primary bg-primary/5' : 'border-border'}`} data-id="element-1339" data-mp="342rmarg6ixwn">
                <header className="mb-3 flex items-center justify-between gap-2 px-1" data-id="element-1340" data-mp="3mt93pzvka72p">
                  <span className="flex min-w-0 items-center gap-2" data-id="element-1341" data-mp="384mjbjr2up4m">
                    <span className="h-2.5 w-2.5 flex-none rounded-full" style={{
                  backgroundColor: stage.color
                }} aria-hidden="true" data-id="element-1342" data-mp="3828ptfc1pkz6" />
                    <h2 className="truncate text-sm font-semibold text-foreground" data-id="element-1343" data-mp="4lt99gxicif7">
                      {stage.name}
                    </h2>
                  </span>
                  <span className="flex flex-none items-center gap-1" data-id="element-1344" data-mp="jabf0wygndt2">
                    <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground" data-id="element-1345" data-mp="eb8b5fu7vdmo">
                      {stageLeads.length}
                    </span>
                    <button type="button" aria-label={`Ocultar columna ${stage.name}`} onClick={() => setHidden(prev => [...prev, stage.id])} className="rounded-md p-1 text-muted-foreground hover:bg-background" data-id="element-1346" data-mp="5aai1slbgqiv">
                      <EyeOffIcon className="h-3.5 w-3.5" data-id="element-1347" data-mp="3rq4x3g8fwxc8" />
                    </button>
                  </span>
                </header>

                <ul className="space-y-2" data-id="element-1348" data-mp="3mrlb5ftujljv">
                  {stageLeads.map(lead => {
                const member = members.find(m => m.userId === lead.assignedTo);
                const note = lastNote(lead.id);
                return <li key={lead.id} data-id="element-1349" data-mp="3dpc0mpykvs6h">
                        <article draggable={canEdit} onDragStart={() => setDragging(lead.id)} onDragEnd={() => setDragging(null)} className={`rounded-xl border border-border bg-background p-3 shadow-sm transition-opacity ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''} ${dragging === lead.id ? 'opacity-50' : ''}`} data-id="element-1350" data-mp="38rage1pf1mtf">
                          <Link to={`/app/leads/${lead.id}`} className="block text-sm font-semibold text-foreground hover:text-primary" data-id="element-1351" data-mp="1y87db421i5ij">
                            {fullName(lead.firstName, lead.lastName)}
                          </Link>
                          {shows('company') && <p className="mt-0.5 truncate text-xs text-muted-foreground" data-id="element-1352" data-mp="1ahl6fg3rura4">
                              {lead.company}
                            </p>}
                          {shows('phone') && <p className="mt-1.5 text-xs text-muted-foreground" data-id="element-1353" data-mp="1ehc320fzc1mk">
                              {lead.phone}
                            </p>}
                          {shows('email') && <p className="truncate text-xs text-muted-foreground" data-id="element-1354" data-mp="mrhc5zch43vs">
                              {lead.email}
                            </p>}
                          {shows('service') && lead.service && <p className="mt-1 truncate text-xs text-foreground" data-id="element-1355" data-mp="3gc66hepqlv22">
                              {lead.service}
                            </p>}
                          {shows('budget') && lead.budget && <p className="text-xs font-medium text-foreground" data-id="element-1356" data-mp="3qallijbhpdv7">
                              {lead.budget}
                            </p>}
                          {shows('note') && note && <p className={`mt-2 line-clamp-2 rounded-md border-l-2 px-2 py-1 text-[11px] leading-snug text-foreground ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1357" data-mp="34cj0ngx4qex">
                              {note.body}
                            </p>}
                          {(cards.showSource || shows('date') || shows('owner') || shows('lastContact')) && <div className="mt-3 flex items-center justify-between gap-2" data-id="element-1358" data-mp="7y6zzj552rab">
                              {cards.showSource ? <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1359" data-mp="hu0mnvcko15t" /> : <span data-id="element-1360" data-mp="hvlhlm2stm47" />}
                              <span className="flex items-center gap-2" data-id="element-1361" data-mp="10ib1rqaqt6q3">
                                {shows('date') && <span className="text-[11px] text-muted-foreground" data-id="element-1362" data-mp="15hfbqj5hr8hh">
                                    {formatDate(lead.createdAt)}
                                  </span>}
                                {shows('lastContact') && lead.lastContactAt && <span className="text-[11px] text-muted-foreground" data-id="element-1363" data-mp="wfo77hmot7p7">
                                    {formatDate(lead.lastContactAt)}
                                  </span>}
                                {shows('owner') && member && <Avatar name={fullName(member.firstName, member.lastName)} size="sm" data-id="element-1364" data-mp="mk4cgyjv0f21" />}
                              </span>
                            </div>}
                        </article>
                      </li>;
              })}
                  {stageLeads.length === 0 && <li className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground" data-id="element-1365" data-mp="3uzebfxsgd45l">
                      Sin leads en este estado
                    </li>}
                </ul>
              </section>;
        })}
        </div>
      </div>

      {/* Panel flotante para ver el embudo mientras se personalizan las tarjetas. */}
      <FloatingPanel open={cardsOpen} title="Personalizar tarjetas" description="Los cambios se aplican en el embudo al instante." onClose={() => setCardsOpen(false)} footer={<Button size="sm" onClick={() => setCardsOpen(false)} data-id="element-1367" data-mp="24vcdpet2hz6o">Listo</Button>} data-id="element-1366" data-mp="1zuewp5jsnb6r">
        <div className="space-y-5" data-id="element-1368" data-mp="2sjcfravtxc0k">
          <div data-id="element-1369" data-mp="2sl6sw30uzzu3">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1370" data-mp="2xhnotjeavihq">Modo</p>
            <div className="grid grid-cols-2 gap-2" data-id="element-1371" data-mp="2xjqeg2bjj9lx">
              {(['compacto', 'ampliado'] as const).map(mode => <button key={mode} type="button" onClick={() => setCards({
              mode
            })} className={`min-h-[44px] rounded-xl border px-4 text-sm font-medium capitalize transition-colors ${cards.mode === mode ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'}`} data-id="element-1372" data-mp="2ir6mlxtucojs">
                  {mode}
                </button>)}
            </div>
            <p className="mt-2 text-xs text-muted-foreground" data-id="element-1373" data-mp="zul7fo60lx5x">
              En modo compacto solo se muestran nombre, empresa y teléfono.
            </p>
          </div>

          <Switch id="pipeline-source" label="Mostrar fuente en las tarjetas" checked={cards.showSource} onChange={showSource => setCards({
          showSource
        })} data-id="element-1374" data-mp="1dnucjcmxjwin" />

          <div data-id="element-1375" data-mp="lvd8mwdrkr29">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1376" data-mp="luty40qg14y9">
              Campos en modo ampliado
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1377" data-mp="uv8ld5mywu4e">
              {CARD_FIELDS.map(field => <li key={field.key} data-id="element-1378" data-mp="ut5n5ybnvuol">
                  <Checkbox id={`card-${field.key}`} label={field.label} disabled={compact} checked={cards.fields.includes(field.key)} onChange={e => setCards({
                fields: e.target.checked ? [...cards.fields, field.key] : cards.fields.filter(f => f !== field.key)
              })} data-id="element-1379" data-mp="pvnnkyjjart9" />
                </li>)}
            </ul>
          </div>
        </div>
      </FloatingPanel>

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1380" data-mp="13wzc7omz2qcm" />
    </>;
}